import { Injectable, EventEmitter } from '@angular/core';
import { RemoteCacheService } from '@base/remote-cache.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DirtyService {
  SAVE_STATE = 'SAVED';

  // Flag to suppress dirty tracking during form initialization
  private isInitializing = false;

  constructor(private remoteCacheService: RemoteCacheService) { }

  _dirty: boolean;

  _saving: boolean;

  _error: boolean;

  detect: EventEmitter<any> = new EventEmitter<any>();

  error: EventEmitter<any> = new EventEmitter<any>();

  dirty: EventEmitter<boolean> = new EventEmitter<boolean>();

  saving: EventEmitter<boolean> = new EventEmitter<boolean>();

  onInit: EventEmitter<any> = new EventEmitter<any>();

  onChange: EventEmitter<any> = new EventEmitter<any>();

  onRefresh: EventEmitter<any> = new EventEmitter<any>();

  saveEmitter: EventEmitter<any> = new EventEmitter<any>();

  enableSaveEmitter: EventEmitter<any> = new EventEmitter<any>();

  // Per-tab dirty state tracking for MDI
  private tabDirtyState = new Map<string, boolean>();

  // Event emitter for tab dirty state changes
  tabDirtyChanged: EventEmitter<{ tabId: string; dirty: boolean }> = new EventEmitter();

  save() {
    this.saveEmitter.emit();
  }

  enableSave(state) {
    this.enableSaveEmitter.emit(state);
  }

  refresh() {
    this.onRefresh.emit();
  }

  init() {
    this.onInit.emit(true);
  }

  change(map) {
    this.onChange.emit(map);
  }

  setDirty() {
    // Skip if we're in initialization phase
    if (this.isInitializing) {
      return;
    }
    this._dirty = true;
    this.dirty.emit(true);
  }

  /**
   * Start initialization phase - suppresses setDirty calls
   */
  startInit() {
    this.isInitializing = true;
  }

  /**
   * End initialization phase and clear any dirty state that may have been set
   */
  endInit() {
    this.isInitializing = false;
    this.clearDirty();
  }

  clearDirty() {
    this._dirty = false;
    this.dirty.emit(false);
    this.clearError();
  }

  setError() {
    this._error = true;
    this.error.emit(true);
  }

  clearError() {
    this._error = false;
    this.error.emit(false);
  }

  setSaving() {
    this._saving = false;
    this.saving.emit(true);
  }

  clearSaving() {
    this._saving = false;
    this.saving.emit(false);
  }

  detectChanges() {
    if (!this._dirty) {
      this.detect.emit();
    }
  }

  async clearRemoteCache(withRemote: boolean) {
    return firstValueFrom(this.remoteCacheService.clear(withRemote));
  }

  // Per-tab dirty state methods for MDI support

  /**
   * Set dirty state for a specific tab
   */
  setTabDirty(tabId: string): void {
    this.tabDirtyState.set(tabId, true);
    this.tabDirtyChanged.emit({ tabId, dirty: true });
  }

  /**
   * Clear dirty state for a specific tab
   */
  clearTabDirty(tabId: string): void {
    this.tabDirtyState.delete(tabId);
    this.tabDirtyChanged.emit({ tabId, dirty: false });
  }

  /**
   * Check if a specific tab has unsaved changes
   */
  isTabDirty(tabId: string): boolean {
    return this.tabDirtyState.get(tabId) ?? false;
  }

  /**
   * Check if any tab has unsaved changes
   */
  hasAnyTabDirty(): boolean {
    return Array.from(this.tabDirtyState.values()).some((dirty) => dirty);
  }

  /**
   * Get all dirty tab IDs
   */
  getDirtyTabIds(): string[] {
    return Array.from(this.tabDirtyState.entries())
      .filter(([, dirty]) => dirty)
      .map(([tabId]) => tabId);
  }
}
